import type { Address } from '@/api/types';

/**
 * The Outbox: messages the user pressed Send on while there was no connection
 * (issue #201, doc 06 §10 "Background sync for queued sends").
 *
 * ## Why a record of its own rather than a draft
 *
 * A draft is something the user is still writing. An outbox entry is something
 * they have finished writing and instructed the app to send; the only reason it
 * is still here is that the network was not. Keeping the two apart is what lets
 * the UI be honest in both directions — a draft is never described as "waiting
 * to send", and a queued message is never described as "Sent".
 *
 * ## What is stored, and why all of it
 *
 * Everything needed to send the message from a cold start, because that is the
 * case the feature exists for: the tab is closed, the laptop is shut, and two
 * hours later the user opens the app on a train with signal. Nothing may be
 * reconstructed from React state, from the query cache, or from the server.
 *
 * - **The whole message.** Recipients (To/Cc/**Bcc** — Bcc exists nowhere else
 *   once the composer is gone), subject, both bodies, threading ids, the
 *   compose mode, and the draft the composer autosaved into so the send can
 *   still retire it.
 * - **The pinned account.** `accountId` *and* `fromAddress`, exactly as
 *   `RestoredSend` carries them. The active account is a global that the user
 *   may well have switched by the time the queue flushes; a queued message must
 *   go out from the mailbox it was written in, and the backend refuses a From
 *   that is not the resolved account's own address anyway.
 * - **Attachment bytes**, not staged ids — see {@link OutboxAttachment}.
 * - **An idempotency key minted at queue time**, see {@link OutboxMessage.idempotencyKey}.
 * - **Timestamps and attempt bookkeeping**, so the backoff survives a reload
 *   rather than restarting from zero on every visit.
 */

/**
 * Where a queued message is in its life.
 *
 * `held` is the undo window (5–30 s, Settings → General; `sendUndoWindowMs`). The record is
 * written to IndexedDB the moment Send is pressed, *before* the window opens,
 * because the undo window commits on unload: if the tab dies mid-window the
 * user's instruction was "send", and an enqueue that only happened at commit
 * time would be an IndexedDB write racing a document teardown — the one write
 * that cannot be relied on. So the durable record comes first and Undo deletes
 * it, which is the same decision `pendingSend.ts` made, taken one layer down.
 *
 * A `held` record is invisible to the flusher. It is promoted to `queued` when
 * the window commits, or — for a record left behind by a tab that died during
 * the window — by {@link promoteExpiredHolds} on the next start.
 *
 * `sending` is a lease, not a state: it names the tab that claimed the record
 * and when, so a tab that crashes mid-flush does not strand the message.
 */
export type OutboxState = 'held' | 'queued' | 'sending' | 'failed';

/** One attachment, carried as bytes because ids do not survive being offline. */
export interface OutboxAttachment {
  /** Stable within the record; the order here is the order on the message. */
  key: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  /**
   * The bytes. A `Blob` structured-clones into IndexedDB natively, so this is
   * stored as-is rather than base64 — base64 would cost 33% more space and a
   * full decode per flush attempt.
   *
   * **Why bytes at all.** The composer stages attachments eagerly and sends
   * only ids (`attachment_ids` in `wireSendInput`), which is right online and
   * useless offline: there is nothing to stage to, and an id staged earlier
   * expires after 24 hours (`attach.DefaultTTL`). So a message queued offline
   * holds its own bytes and re-stages them at flush time.
   *
   * Absent in exactly one case: an attachment this client never had the bytes
   * for — a draft reopened from the server, whose files the backend re-staged
   * and the browser only ever saw as ids. Those carry {@link stagedId} alone
   * and are re-used as-is; if the staging TTL beats the reconnection the
   * message fails **visibly**, in the Outbox, saying which file went.
   */
  blob?: Blob | undefined;
  /**
   * The staged id: either one the message arrived with (a draft's attachment),
   * or the one a flush attempt uploaded the bytes to. Either way it is kept so
   * a retry re-sends the *same* body — see {@link OutboxMessage.idempotencyKey}.
   */
  stagedId?: string | undefined;
}

/** Why a message stopped, in the user's words and the server's. */
export interface OutboxFailure {
  /** Shown to the user. A sentence, not a code. */
  message: string;
  /** The API error code, when there was one. For support, not for display. */
  code?: string;
  /** HTTP status, 0 for "never reached the server". */
  status?: number;
  /** Addresses the server refused, when that is what went wrong. */
  rejected?: { address: string; message: string }[];
  at: number;
}

export interface OutboxMessage {
  /** Primary key. Also the React key and the value in `?message=` on the view. */
  id: string;
  /** The mailbox this was composed in and must go out from. */
  accountId: string;
  fromAddress: string;
  /**
   * The signed-in owner (`Session.account.id`) this message belongs to.
   *
   * Stamped when the message is queued, and — for a record written before the
   * field existed — when a revoked session keeps the Outbox (ADR 0061 §4).
   * A record is visible to, editable by and sendable from **its owner's
   * sessions only**: after a revocation the queue stays on the device so it
   * can go out when the same person signs back in, and a different person
   * signing in on that device must neither see nor send it. `undefined` is a
   * legacy record, treated as the current session's.
   */
  ownerId?: string | undefined;

  to: Address[];
  cc: Address[];
  bcc: Address[];
  subject: string;
  bodyHtml: string;
  bodyText: string;
  inReplyToMessageId?: string | undefined;
  forwardedMessageId?: string | undefined;
  mode: 'new' | 'reply' | 'reply-all' | 'forward';
  /** The autosaved draft this was composed in, so the send can retire it. */
  draftId?: string | undefined;

  attachments: OutboxAttachment[];
  /**
   * The Nextcloud link shares whose block is in `bodyHtml` (ADR 0084 D13),
   * carried like staged ids so the send marks them sent. Ids, not bytes: the
   * files are already on Nextcloud, uploaded at attach time (D9).
   */
  nextcloudUploadIds?: string[] | undefined;

  /**
   * Minted once, here, and reused on every attempt.
   *
   * The backend remembers `(owner, Idempotency-Key)` for 24 hours and replays
   * the first answer (ADR 0014, `internal/httpapi/idempotency.go`), so a retry
   * after a response we never saw cannot send twice. The transport mints its
   * own key per call, which is right for a single button press and useless
   * here: a flush after a reload is a different call and would get a different
   * key. So the key is part of the record, and `sendDirect` takes it.
   *
   * The server fingerprints `sha256(method || path || body)` and refuses a key
   * reused with a *different* body. Two consequences the rest of this feature
   * has to honour:
   *
   *  1. Attachments are staged once and their ids kept on the record, so every
   *     attempt sends a byte-identical body.
   *  2. **Editing a queued message mints a new key.** The body has genuinely
   *     changed, and the old key names a request the server may still answer.
   */
  idempotencyKey: string;

  state: OutboxState;
  createdAt: number;
  /** When the state last changed. Drives the "held" promotion and the lease. */
  updatedAt: number;
  /** How many times a send has been attempted. Survives reloads. */
  attempts: number;
  /** When the last attempt ran, 0 for none. */
  lastAttemptAt: number;
  /** Not before this moment — the backoff, persisted rather than in a timer. */
  nextAttemptAt: number;
  /** Set while `state === 'sending'`: which tab holds the lease. */
  claimedBy?: string | undefined;
  /**
   * While `state === 'held'`, the moment the hold lapses.
   *
   * Two things hold a message: the five-second undo window (`createdAt + 5s`)
   * and an open composer editing it. Both are a promise that some tab is about
   * to decide; neither may outlive the tab that made it, because the user's
   * standing instruction is "send this". So the hold carries its own deadline,
   * an editing composer refreshes it while it is open, and
   * {@link recoverOutbox} puts a lapsed hold back on the queue — with the
   * message's original content, which is the last thing the user actually
   * asked for.
   */
  holdUntil?: number | undefined;
  failure?: OutboxFailure | undefined;
}

/** What the composer hands over. Everything else is minted by the store. */
export type OutboxDraft = Omit<
  OutboxMessage,
  | 'id'
  | 'ownerId'
  | 'idempotencyKey'
  | 'state'
  | 'createdAt'
  | 'updatedAt'
  | 'attempts'
  | 'lastAttemptAt'
  | 'nextAttemptAt'
  | 'claimedBy'
  | 'holdUntil'
  | 'failure'
>;

/**
 * The per-message ceiling on attachment bytes.
 *
 * Deliberately the server's own `mail.max_attachment_total_bytes` default
 * (`backend/internal/attach/store.go`, `DefaultMaxTotalBytes = 24 << 20`). A
 * message the server would refuse at flush time must be refused *now*, while
 * the composer is still open and the user can do something about it — the whole
 * point of a cap is that it is hit at the moment of the decision, not two hours
 * later on a train.
 */
export const MAX_MESSAGE_ATTACHMENT_BYTES = 24 * 1024 * 1024;

/**
 * The ceiling on everything the Outbox holds, across all accounts.
 *
 * IndexedDB is not free and a browser that hits its origin quota evicts the
 * whole database, taking messages with it — the silent loss this feature
 * exists to prevent. Three messages at the per-message ceiling is a deliberate
 * choice: generous enough that no ordinary day of offline mail touches it,
 * small enough to stay well inside a default origin quota beside the query
 * cache the read side persists.
 *
 * When the next message would cross it, the send is **refused at the composer**
 * with a sentence naming the Outbox — the composer stays open with the message
 * in it. It is never accepted-then-dropped, and it never fails at flush time.
 */
export const MAX_OUTBOX_BYTES = 72 * 1024 * 1024;

/** The most messages the Outbox will hold, whatever they weigh. */
export const MAX_OUTBOX_MESSAGES = 100;

/** Bytes an outbox record occupies, near enough to enforce a cap with. */
export function outboxMessageBytes(message: {
  attachments: { sizeBytes: number }[];
  bodyHtml: string;
  bodyText: string;
}): number {
  const attachments = message.attachments.reduce((total, one) => total + one.sizeBytes, 0);
  return attachments + message.bodyHtml.length + message.bodyText.length;
}
